import {
  IFileUploadController,
  MK_FILE_OBJECTS,
  MK_FILE_UPLOAD_CONFIG,
  MK_FILE_UPLOAD_STATES
} from "./IFileUploadController";
import {AbstractController} from "../../../controllers/AbstractController";
import {inject, signal} from "@angular/core";
import {ControllerEventBus} from "../../../event-buses/controller.event-bus";
import {ControllerEventName} from "../../../event-buses/ControllerEventName";
import {ControllerEvent} from "../../../event-buses/ControllerEvent";
import {FileObject} from "../../../interfaces/domain/FileObject";
import {FileMetaData} from "../../../interfaces/rest/FileMetaData";
import {FileType} from "../../../interfaces/rest/FileType";
import {FileRepository} from "../../../repositories/file.repository";
import {StringIndex} from "../../../interfaces/generic/StringIndex";
import {Helper} from "../../../helpers/Helper";

export abstract class AbstractFileUploadController extends AbstractController implements IFileUploadController {

  private fileIdSequence = 0;

  private readonly fileRepository: FileRepository;

  protected constructor(controllerEventBus: ControllerEventBus) {
    super(controllerEventBus);

    this.fileRepository = inject(FileRepository);

    this.initModels();
  }

  protected initModels() {
    this.addModel(MK_FILE_OBJECTS, {
      fileObjects$: signal([]),
    });

    this.addModel(MK_FILE_UPLOAD_CONFIG, {
      noBackend$: signal(this.isDefaultNoBackend()),
      multiple$: signal(this.isDefaultMultiple()),
      fileSize$: signal(this.getDefaultFileSizeMB()),
      acceptedFileExtensions$: signal(this.getDefaultAcceptedFileExtensions()),
      documentFileTypes$: signal(this.getDefaultDocumentFileTypes()),
      backendFileType$: signal(this.getDefaultBackendFileType()),
      minWidth$: signal(this.getDefaultMinWidth()),
      maxWidth$: signal(this.getDefaultMaxWidth()),
      minHeight$: signal(this.getDefaultMinHeight()),
      maxHeight$: signal(this.getDefaultMaxHeight()),
      additionalRequirementMessages$: signal([]),
      streamingFileUpload$: signal(false),
    });

    this.addModel(MK_FILE_UPLOAD_STATES, {
      filesLoading$: signal(false),
      overwriteError$: signal(false),
      extErrors$: signal([]),
      sizeErrors$: signal([]),
      resolutionErrors$: signal([]),
      rawFileLoaded$: signal(null),
      fileLoaded$: signal(null),
      fileRemoved$: signal(null),
    });
  }

  handleConfigOverwrite(eventData: any): void {
    const configModel = this.getModel(MK_FILE_UPLOAD_CONFIG);

    if(eventData['noBackend'] !== null && eventData['noBackend'] !== undefined) {
      configModel.noBackend$.set(eventData['noBackend']);
    }
    if(eventData['multiple'] !== null && eventData['multiple'] !== undefined) {
      configModel.multiple$.set(eventData['multiple']);
    }
    if(eventData['fileSize'] !== null && eventData['fileSize'] !== undefined) {
      configModel.fileSize$.set(eventData['fileSize']);
    }
    if(eventData['acceptedFileExtensions'] !== null && eventData['acceptedFileExtensions'] !== undefined) {
      configModel.acceptedFileExtensions$.set(eventData['acceptedFileExtensions']);
    }
    if(eventData['documentFileTypes'] !== null && eventData['documentFileTypes'] !== undefined) {
      configModel.documentFileTypes$.set(eventData['documentFileTypes']);
    }
    if(eventData['backendFileType'] !== null && eventData['backendFileType'] !== undefined) {
      configModel.backendFileType$.set(eventData['backendFileType']);
    }
    if(eventData['minWidth'] !== null && eventData['minWidth'] !== undefined) {
      configModel.minWidth$.set(eventData['minWidth']);
    }
    if(eventData['maxWidth'] !== null && eventData['maxWidth'] !== undefined) {
      configModel.maxWidth$.set(eventData['maxWidth']);
    }
    if(eventData['minHeight'] !== null && eventData['minHeight'] !== undefined) {
      configModel.minHeight$.set(eventData['minHeight']);
    }
    if(eventData['maxHeight'] !== null && eventData['maxHeight'] !== undefined) {
      configModel.maxHeight$.set(eventData['maxHeight']);
    }
    if(eventData['additionalRequirementMessages'] !== null && eventData['additionalRequirementMessages'] !== undefined) {
      configModel.additionalRequirementMessages$.set(eventData['additionalRequirementMessages']);
    }
    if(Helper.isValid(eventData['streamingFileUpload'])) {
      configModel.streamingFileUpload$.set(eventData['streamingFileUpload']);
    }
  }

  handleLoadFiles(eventData: any): void {
    this.clearFileObjects();

    if(eventData['files'] === null || eventData['files'] === undefined || eventData['files'].length === 0) {
      return;
    }

    const statesModel = this.getModel(MK_FILE_UPLOAD_STATES);
    const fileObjects: Array<FileObject> = eventData['files'];

    statesModel.filesLoading$.set(true);

    fileObjects.forEach((_fileObject) => {
      const fileObject = Object.assign({}, _fileObject);
      fileObject.internalFileId = this.nextFileId();

      if(fileObject.fileMetadata && fileObject.fileMetadata.fileId) {
        this.fileRepository.getFile(fileObject.fileMetadata.fileId).subscribe({
          next: (result) => {
            if(result) {
              fileObject.name = result.fileName || 'FileWithNoName';
              fileObject.fileMetadata = result;
              fileObject.isLoading = false;

              this.addFileObject(fileObject);
            }

            // IMPROVEMENT: Should ideally be waiting for ALL files to be loaded before setting this flag.
            statesModel.filesLoading$.set(false);
          }
        });
      }
      else {
        fileObject.isLoading = false;
        setTimeout(() => {
          this.addFileObject(fileObject);
        }, 0);
        statesModel.filesLoading$.set(false);
      }
    });
  }

  handleRemoveFile(eventData: any): void {
    const fileObject = eventData['fileObject'];

    if(fileObject) {
      this.removeFileObject(fileObject);
      this.emit(new ControllerEvent(ControllerEventName.FileObjectRemoved, {fileObject: fileObject}));
      this.getModel(MK_FILE_UPLOAD_STATES).fileRemoved$.set(fileObject);
    }
  }

  handleFileInput(eventData: any): void {
    if(!eventData['files']) {
      return;
    }

    const files = eventData['files'];
    const configModel = this.getModel(MK_FILE_UPLOAD_CONFIG);
    const fileObjectsModel = this.getModel(MK_FILE_OBJECTS);
    const statesModel = this.getModel(MK_FILE_UPLOAD_STATES);

    if(!configModel.multiple$() && fileObjectsModel.fileObjects$().length === 1) {
      statesModel.overwriteError$.set(true);
      return;
    }

    this.resetErrors();

    if(files.length > 0) {
      if(files.length === 1) {
        this.processFile(files[0]);
      }
      else {
        for(let i = 0; i < files.length; i++) {
          this.processFile(files[i]);
        }
      }
    }
  }

  protected processFile(file: any) {
    if(!this.isFileExtAccepted(file.name.toLowerCase())) {
      this.addExtError(file.name);
      return;
    }

    // dwg type is not the same on different browser
    // set the ext type forcefully
    if(!file.type || file.type.indexOf('dwg') > -1) {
      const ext = this.extractExt(file.name.toLowerCase());
      if(ext) {
        let value;
        const acFiles = ['iam', 'dwf', 'dwg', 'dxf', 'json']; // autocad files

        if(acFiles.indexOf(ext) > -1) {
          value = 'application/octet-stream';
        }

        Object.defineProperty(file, 'type', {
          value: value
        });
      }
      else {
        this.addExtError(file.name);
        return;
      }
    }

    // validate file size.
    if(!this.isFileSizeAcceptable(file.size)) {
      this.addSizeError(file.name);
      return;
    }

    this.emit(new ControllerEvent(ControllerEventName.RawFileLoaded, {file: file}));
    this.getModel(MK_FILE_UPLOAD_STATES).rawFileLoaded$.set(file);

    if(this.isImageFile(file)) {
      if(file.type.indexOf('dxf') === -1) {
        const img = new Image();

        img.onload = () => {
          if(!this.isImageDimensionAcceptable(img.width, img.height)) {
            this.addResolutionError(file.name);
          }
          else {
            this.processFileData(file);
          }
        };
        img.src = window.URL.createObjectURL(file)

        return;
      }
    }

    this.processFileData(file);
  }

  protected processFileData(file: any): void {
    const configModel = this.getModel(MK_FILE_UPLOAD_CONFIG);

    if(configModel.streamingFileUpload$() && !configModel.noBackend$()) {
      const currentFileId = this.nextFileId();
      const fileType = this.determineFileType(file) || FileType.Document;
      const fileObject: FileObject = {
        name: file.name,
        isLoading: true,
        internalFileId: currentFileId,
        rawFile: file,
      };
      fileObject.fileMetadata = new FileMetaData();
      fileObject.fileMetadata.fileName = file.name;
      fileObject.fileMetadata.fileTitle = file.name;
      fileObject.fileMetadata.fileType = fileType;

      this.addFileObject(fileObject);

      this.fileRepository.streamingFileUpload(file, fileType).subscribe({
        next: (result) => {
          fileObject.fileMetadata = result;
          fileObject.isLoading = false;

          this.emit(new ControllerEvent(ControllerEventName.FileObjectLoaded, {fileObject: fileObject}));
          this.getModel(MK_FILE_UPLOAD_STATES).fileLoaded$.set(fileObject);
        },
        error: (error) => {
          this.errorToast(`Failed to upload file`);
        }
      });
    }
    else {
      const reader = new FileReader();
      reader.onload = () => {
        const currentFileId = this.nextFileId();
        let fileBase64 = reader.result!.toString();
        fileBase64 = fileBase64.substring(fileBase64.indexOf(',') + 1);

        const fileObject: FileObject = {
          name: file.name,
          isLoading: true,
          internalFileId: currentFileId,
          base64Contents: fileBase64,
          rawFile: file,
        };
        fileObject.fileMetadata = new FileMetaData();
        fileObject.fileMetadata.fileName = file.name;
        fileObject.fileMetadata.fileTitle = file.name;
        fileObject.fileMetadata.fileType = this.determineFileType(file);
        fileObject.fileMetadata.fileByte = fileBase64;

        if(configModel.noBackend$()) {
          fileObject.isLoading = false;
          this.addFileObject(fileObject);

          this.emit(new ControllerEvent(ControllerEventName.FileObjectLoaded, {fileObject: fileObject}));
          this.getModel(MK_FILE_UPLOAD_STATES).fileLoaded$.set(fileObject);
        }
        else {
          this.addFileObject(fileObject);

          this.fileRepository.addFile(fileObject.fileMetadata, configModel.backendFileType$())
            .subscribe({
              next: (result: FileMetaData) => {
                fileObject.fileMetadata = result;
                fileObject.isLoading = false;

                this.emit(new ControllerEvent(ControllerEventName.FileObjectLoaded, {fileObject: fileObject}));
                this.getModel(MK_FILE_UPLOAD_STATES).fileLoaded$.set(fileObject);
              },
              error: (error) => {
                this.errorToast(`Failed to upload file`);
              }
            });
        }
      };

      reader.readAsDataURL(file);
    }
  }

  protected addFileObject(fileObject: FileObject): void {
    const model = this.getModel(MK_FILE_OBJECTS);
    model.fileObjects$.set(model.fileObjects$().concat(fileObject));
  }

  protected removeFileObject(fileObject: FileObject): void {
    const model = this.getModel(MK_FILE_OBJECTS);
    const fileObjects = model.fileObjects$();

    model.fileObjects$.set(fileObjects.filter((fo) => fo.internalFileId !== fileObject.internalFileId));
  }

  protected clearFileObjects(): void {
    const model = this.getModel(MK_FILE_OBJECTS);
    model.fileObjects$.set([]);
  }

  protected determineFileType(file: File): FileType|null {
    const customFileType = this.determineCustomFileType();

    if(customFileType) {
      return customFileType;
    }

    if(this.checkDocumentFileType(file)) {
      return FileType.Document;
    }
    else if(file.type && file.type.includes('image')) {
      return FileType.Image;
    }

    return null;
  }

  protected determineCustomFileType(): FileType|null {
    const backendFileType = this.getModel(MK_FILE_UPLOAD_CONFIG).backendFileType$();
    if(backendFileType && backendFileType in FileType) {
      return (FileType as StringIndex)[backendFileType];
    }

    return null;
  }

  protected checkDocumentFileType(file: File): boolean {
    const acceptedTypes = this.getModel(MK_FILE_UPLOAD_CONFIG).documentFileTypes$();
    for(let i = 0; i < acceptedTypes.length; i++) {
      if(file.type && file.type.includes(acceptedTypes[i])) {
        return true;
      }
    }

    return false;
  }

  protected isImageFile(file: any) {
    const fileType = file.type;

    if(!fileType) {
      return false;
    }

    return fileType.indexOf('image') > -1;
  }

  protected isImageDimensionAcceptable(width: number, height: number): boolean {
    const configModel = this.getModel(MK_FILE_UPLOAD_CONFIG);
    const minWidth = configModel.minWidth$();
    const maxWidth = configModel.maxWidth$();
    const minHeight = configModel.minHeight$();
    const maxHeight = configModel.maxHeight$();

    if(minWidth && width < minWidth) {
      return false;
    }
    if(maxWidth && width > maxWidth) {
      return false;
    }
    if(minHeight && height < minHeight) {
      return false;
    }
    if(maxHeight && height > maxHeight) {
      return false;
    }

    return true;
  }

  protected extractExt(fileName: string): string {
    if(!fileName) {
      return '';
    }

    const parts = fileName.split('.');
    if(!parts) {
      return '';
    }

    return parts[parts.length - 1];
  }

  protected isFileExtAccepted(path: string): boolean {
    const parts = path.split('/');
    const fileName = parts[parts.length - 1];

    const acceptedExtensions = this.getModel(MK_FILE_UPLOAD_CONFIG).acceptedFileExtensions$();

    for(let i = 0; i < acceptedExtensions.length; i++) {
      if(fileName.indexOf(acceptedExtensions[i]) > -1) {
        return true;
      }
    }

    return false;
  }

  protected isFileSizeAcceptable(fileSize: number): boolean {
    if(fileSize <= 0) {
      return false;
    }

    const maxSizeMB = this.getModel(MK_FILE_UPLOAD_CONFIG).fileSize$();

    if((fileSize / 1e6) > maxSizeMB) {
      return false;
    }

    return true;
  }

  protected resetErrors(): void {
    const statesModel = this.getModel(MK_FILE_UPLOAD_STATES);

    statesModel.overwriteError$.set(false);
    statesModel.extErrors$.set([]);
    statesModel.sizeErrors$.set([]);
    statesModel.resolutionErrors$.set([]);
  }

  protected addExtError(fileName: string): void {
    const statesModel = this.getModel(MK_FILE_UPLOAD_STATES);
    statesModel.extErrors$.set(statesModel.extErrors$().concat(fileName));
  }

  protected addSizeError(fileName: string): void {
    const statesModel = this.getModel(MK_FILE_UPLOAD_STATES);
    statesModel.sizeErrors$.set(statesModel.sizeErrors$().concat(fileName));
  }

  protected addResolutionError(fileName: string): void {
    const statesModel = this.getModel(MK_FILE_UPLOAD_STATES);
    statesModel.resolutionErrors$.set(statesModel.resolutionErrors$().concat(fileName));
  }

  protected nextFileId(): number {
    return this.fileIdSequence++;
  }

  protected isDefaultNoBackend(): boolean {
    return false;
  }

  protected isDefaultMultiple(): boolean {
    return false;
  }

  protected getDefaultFileSizeMB(): number {
    return 64;
  }

  protected getDefaultAcceptedFileExtensions(): Array<string> {
    return [];
  }

  protected getDefaultDocumentFileTypes(): Array<string> {
    return [
      'pdf',
      'html',
      'dxf',
      'dwg',
      'iam',
      'dwf',
      'octet-stream',
      'xlsx',
      'msword',
      'vnd.ms-excel',
      'vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'vnd.openxmlformats-officedocument.spreadsheetml.template',
      'vnd.ms-excel.sheet.macroEnabled.12',
      'vnd.ms-excel.template.macroEnabled.12',
      'vnd.ms-excel.addin.macroEnabled.12',
      'vnd.ms-excel.sheet.binary.macroEnabled.12',
    ];
  }

  protected getDefaultBackendFileType(): string {
    return 'all';
  }

  protected getDefaultMinWidth(): number|null {
    return null;
  }

  protected getDefaultMaxWidth(): number|null {
    return null;
  }

  protected getDefaultMinHeight(): number|null {
    return null;
  }

  protected getDefaultMaxHeight(): number|null {
    return null;
  }
}
